import React from 'react';
import PropTypes, { oneOf } from 'prop-types';
import IDSContext from '@ids/context';
import ZIndex from '@ids/zindex';
import { themes, getTheme, ThemeContext } from '@ids/theme';
import Position, { RootRef } from '@ids/position';
import { styleOverrideReason } from '@ids/core';
import MenuList from './MenuList';

const {
  bool,
  func,
  node,
  string,
  number,
  object,
  oneOfType,
  array
} = PropTypes;

/** */
export default class Menu extends React.PureComponent {
  static contextType = ThemeContext;

  static propTypes = {
    /**
     * Alignment  to show the Menu, value is string of alignments
     * vertical: 'top', 'bottom', 'middle'
     * horizontal: 'left', 'right', 'center'
     * NOTE: horizontal alignments cannot be paired with horizontal positions
     */
    alignment: string,
    /**
     * The element used to set the position of showing the Menu.
     */
    anchorElement: node.isRequired,
    /**
     * anchorElementRefPropName: a string varible
     * If the anchor element is a React component, and it needs to an alternative reference, so setting the
     * "anchorElementRefPropName" in order to let popper can forward the ref to call. For example: if anchor
     * element is IDS TextField, then the "anchorElementRefPropName" would be "assignLabelRef" or "assignInputRef"
     * which is a prop function to get the text field DOM reference from the TextField.
     */
    anchorElementRefPropName: string,
    /**
     * aria-label
     */
    'aria-label': string,
    /**
     * Determine whether focus on a menu-item when Menu opens
     */
    // eslint-disable-next-line react/boolean-prop-naming
    autoFocus: bool,
    /**
     * Menu contents, normally are `MenuItem`s.
     */
    children: node,
    /**
     * Class to be assigned to Menu. Use to extend the style.
     */
    className: string,
    /**
     * Determine whether Menu is disable
     */
    // eslint-disable-next-line react/boolean-prop-naming
    disabled: bool,
    /**
     * Determine whether MenuItem can be searched by typing
     */
    // eslint-disable-next-line react/boolean-prop-naming
    disableSearch: bool,
    /**
     * A callback function to get index of current selected item in Menu
     */
    getSelectedIndex: func,
    /**
     * ID to be applied to the menu.
     */
    id: string,
    /**
     * Determine whether the MenuItem is highlighted
     */
    highlightIndex: number,
    /**
     * Determine the maximum width of Menu, unit is px.
     */
    maxHeight: string,
    /**
     * Determine the minimum width of Menu, unit is px.
     */
    minWidth: string,
    /**
     * An ARIA role to be assigned to MenuItem.
     */
    menuItemRole: string,
    /**
     * An ARIA role to be assigned to Menu.
     */
    menuRole: string,
    /**
     * Callback fired when the Menu wants to close
     */
    onClose: func,
    /**
     * Callback fired when the mouse hover the Menu
     */
    onMouseEnter: func,
    /**
     * onSelect callback receive from parent to handle open/close, get selectedItem
     */
    onSelect: func,
    /**
     * If `true`, the Menu shows.
     */
    open: bool.isRequired,
    /**
     * Position to show the Menu, value is string of alignments
     * vertical: 'top', 'bottom'
     * horizontal: 'left', 'right'
     * NOTE: horizontal alignments cannot be paired with horizontal positions
     */
    position: string,
    /**
     * preventMenuOverflow: an object variable; used to prevent Menu from being positioned outside the boundary.
     *  More detail in @ids/position preventOverflow API document.
     *
     */
    // eslint-disable-next-line react/forbid-prop-types
    preventMenuOverflow: object,
    /**
     * Determine which search type to apply while typing
     * full - search sequentially adding typed symbold from the beginning
     * first - mimic all behavior to match first char only
     * partial - search for the first occurence in the middle of displayed items
     */
    searchMode: oneOf(['full', 'first', 'partial']),
    /**
     * The optional styles applied to the position node wrapped around the root node.
     */
    // eslint-disable-next-line react/forbid-prop-types
    stylePosition: object,
    /**
     * For highlight current selected item in Menu
     */
    selectedItem: oneOfType([string, array]),
    /**
     * Showing CheckMark on current selected item in Menu
     */
    // eslint-disable-next-line react/boolean-prop-naming
    showCheckmark: bool,
    /**
     * Callback that returns the ref for menu
     */
    reference: func,
    /**
     * Callback that returns the react dom for menu.
     */
    refReactDom: func,
    /**
     * Scroll current selected item into view from Menu if necessary
     */
    // eslint-disable-next-line react/boolean-prop-naming
    scrollToSelected: bool,
    /**
     * When `style` or `className` prop is provided, provide the reason behind the override.
     */
    // eslint-disable-next-line react/no-unused-prop-types
    styleOverrideReason,
    /**
     * When there is an application level theme defined, use this prop to override
     * (e.g. 'mint', 'proconnect', 'quickbooks', 'turbotax')
     */
    theme: oneOf(themes)
  };

  static defaultProps = {
    alignment: 'left',
    anchorElementRefPropName: '',
    'aria-label': undefined,
    autoFocus: false,
    children: undefined,
    className: undefined,
    disabled: false,
    disableSearch: false,
    getSelectedIndex() {},
    highlightIndex: -1,
    id: undefined,
    minWidth: undefined,
    maxHeight: undefined,
    menuItemRole: 'menuitem',
    menuRole: 'menu',
    onClose() {},
    onMouseEnter() {},
    onSelect() {},
    position: 'bottom',
    preventMenuOverflow: undefined,
    searchMode: 'first',
    stylePosition: null,
    selectedItem: undefined,
    showCheckmark: false,
    reference() {},
    refReactDom() {},
    scrollToSelected: false,
    styleOverrideReason: undefined,
    theme: undefined
  };

  componentDidMount() {
    if (this.props.open) {
      this.forceUpdate();
    }
  }

  onRootRef = (ref, refCallback) => {
    this.target = ref;
    if (typeof refCallback === 'function') {
      refCallback(ref);
    }
  };

  // to close menu when use tab/Esc
  handleKeyDown = event => {
    event.persist();
    if (event.key === 'Tab' || event.key === 'Escape') {
      event.preventDefault();
      this.props.onClose(event);
    }
  };

  // if open and current focus element is not inside the Menu, then close
  handleMenuBlur = event => {
    // IE 11 identifies the current element via activeElement
    const activeElement = event.relatedTarget || document.activeElement;
    const { className } = activeElement;
    if (
      this.props.open &&
      !(
        className.indexOf('StyledMenu') > -1 ||
        className.indexOf('StyledMenu__MenuItem') > -1 ||
        className.indexOf('StyledTextField__Input') > -1
      )
    ) {
      this.props.onClose(event);
    }
  };

  // Prevent the activeElement from being changed when click on a disabled mene-item
  handleDisabledItem = child => event => {
    event.preventDefault();
    if (child.props.onMouseDown) {
      child.props.onMouseDown(event);
    }
  };

  render() {
    const {
      alignment,
      anchorElement,
      anchorElementRefPropName,
      autoFocus,
      children,
      className,
      disabled,
      disableSearch,
      getSelectedIndex,
      id,
      highlightIndex,
      minWidth,
      maxHeight,
      menuItemRole,
      menuRole,
      open,
      position,
      preventMenuOverflow,
      selectedItem,
      showCheckmark,
      reference,
      refReactDom,
      onMouseEnter,
      onSelect,
      scrollToSelected,
      theme,
      searchMode,
      // eslint-disable-next-line react/prop-types
      style,
      stylePosition
    } = this.props;

    const { currentTheme } = getTheme({ theme }, this.context);

    // items are MenuItems passed to Menu
    const items = React.Children.map(children, (child, index) => {
      const isSelected =
        (selectedItem && selectedItem === child.props.value) ||
        (Array.isArray(selectedItem) &&
          selectedItem.includes(child.props.value)) ||
        child.props.selected;
      const isHighlighted = highlightIndex === index;
      if (isSelected) {
        getSelectedIndex(index);
      }

      return React.cloneElement(child, {
        'aria-disabled': child.props.disabled,
        checkmark: showCheckmark,
        disabled: disabled || child.props.disabled,
        highlighted: isHighlighted,
        onMouseDown:
          disabled || child.props.disabled
            ? this.handleDisabledItem(child)
            : child.props.onMouseDown,
        onSelect: disabled || child.props.disabled ? null : onSelect,
        role: menuItemRole,
        selected: isSelected,
        theme: currentTheme
      });
    });

    const positionElement = (
      <Position
        alignment={alignment}
        position={position}
        preventOverflow={preventMenuOverflow}
        show={open}
        targetElement={this.target}
        style={stylePosition}
        id={id ? `idsMenu-position-${id}` : undefined}
      >
        <MenuList
          ref={refReactDom}
          aria-label={this.props['aria-label']}
          autoFocus={autoFocus}
          className={className}
          disableSearch={disableSearch}
          searchMode={searchMode}
          id={id}
          minWidth={minWidth}
          maxHeight={maxHeight}
          menuRole={menuRole}
          reference={reference}
          scrollToSelected={scrollToSelected}
          style={style}
          theme={currentTheme}
          onKeyDown={this.handleKeyDown}
          onBlur={this.handleMenuBlur}
          onMouseEnter={onMouseEnter}
        >
          {items}
        </MenuList>
      </Position>
    );

    const cloneAnchor = anchorElementRefPropName ? (
      React.cloneElement(anchorElement, {
        [anchorElementRefPropName]: ref => {
          this.onRootRef(ref, anchorElement.props[anchorElementRefPropName]);
        }
      })
    ) : (
      <RootRef rootRef={this.onRootRef}>{anchorElement}</RootRef>
    );

    return (
      <>
        {cloneAnchor}
        {// eslint-disable-next-line no-negated-condition
        !stylePosition ? (
          <IDSContext.Consumer>
            {context => (
              <ZIndex
                assignZIndex={open}
                getCurrentBaseZIndex={context.getCurrentBaseZIndex}
                incrementBaseZIndex={context.incrementBaseZIndex}
                decrementBaseZIndex={context.decrementBaseZIndex}
              >
                {positionElement}
              </ZIndex>
            )}
          </IDSContext.Consumer>
        ) : (
          positionElement
        )}
      </>
    );
  }
}
